import React, { useEffect, useState } from "react";
import { IoCloseSharp } from "react-icons/io5";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { Col, Container, Row, Card, CardBody, Badge, Table, Spinner, Button } from "reactstrap";
import {
    FiTag, FiTruck, FiDollarSign,
    FiSettings, FiMessageSquare, FiBox, FiMaximize,
    FiInfo, FiCalendar, FiFileText, FiImage, FiActivity, FiLink, FiCheckCircle, FiXCircle
} from "react-icons/fi";
import PageName from "../../components/react_helmet/PageName";
import LogTable from "../../components/table/LogTable";
import { stringifySpecs } from "../../firebase/utility";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { getAdminProductDetails } from "../../api/products.api";
import toast from "react-hot-toast";
import SkeletonLoader from "../../components/loaders/SkeletonLoader";

const ProductDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const { id: slug } = useParams();
    const [newItem, setNewItem] = useState();
    const [loading, setLoading] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [loadingReviews, setLoadingReviews] = useState(false);

    const fetchProductReviews = async (productId) => {
        if (!productId) return;
        setLoadingReviews(true);
        try {
            const snapshot = await firebase.firestore().collection("Reviews")
                .where("productId", "==", productId)
                .get();
            const data = snapshot.docs.map(doc => ({
                ...doc.data(),
                _id: doc.id
            }));
            const sortedData = data.sort((a, b) => {
                const dateA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt);
                const dateB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt);
                return dateB - dateA;
            });
            setReviews(sortedData);
        } catch (error) {
            console.error("Error fetching reviews:", error);
            setReviews([]);
        } finally {
            setLoadingReviews(false);
        }
    };

    const fetchProductDetails = (productSlug) => {
        setLoading(true);
        getAdminProductDetails(productSlug, (res) => {
            if (res.status === 200) {
                setNewItem(res.data);
                fetchProductReviews(res.data?._id);
            } else {
                toast.error(res.message || "Failed to fetch product details");
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        if (location?.state) {
            setNewItem(location?.state);
            fetchProductReviews(location?.state?._id);
        } else if (slug) {
            fetchProductDetails(slug);
        }
    }, [location, slug]);

    if (loading) {
        return (
            <div className="pb-5 min-vh-100 p-4 p-md-5" style={{ backgroundColor: '#f8f9fa' }}>
                <SkeletonLoader height="80px" className="mb-4" borderRadius="10px" />
                <div className="d-flex flex-column flex-md-row gap-4 mb-4">
                    <SkeletonLoader height="160px" width="160px" borderRadius="16px" />
                    <div className="flex-grow-1 d-flex flex-column justify-content-center">
                        <SkeletonLoader height="30px" width="60%" className="mb-3" />
                        <SkeletonLoader height="20px" width="40%" />
                    </div>
                </div>
                <div className="row g-4 mb-4">
                    <div className="col-md-3"><SkeletonLoader height="120px" borderRadius="16px" /></div>
                    <div className="col-md-3"><SkeletonLoader height="120px" borderRadius="16px" /></div>
                    <div className="col-md-3"><SkeletonLoader height="120px" borderRadius="16px" /></div>
                    <div className="col-md-3"><SkeletonLoader height="120px" borderRadius="16px" /></div>
                </div>
                <SkeletonLoader height="300px" borderRadius="16px" />
            </div>
        );
    }

    // Modern Components
    const SectionHeader = ({ icon: Icon, title }) => (
        <div className="d-flex align-items-center gap-3 mb-4 border-bottom pb-3">
            <div className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded shadow-sm" style={{ width: '40px', height: '40px', color: '#0d6efd', backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                <Icon size={20} />
            </div>
            <h5 className="fw-bolder text-dark mb-0 fs-5" style={{ letterSpacing: '-0.5px' }}>{title}</h5>
        </div>
    );

    const DetailItem = ({ label, value, isUrl }) => (
        <div className="mb-4">
            <div className="text-muted small fw-bold text-uppercase mb-1" style={{ letterSpacing: '0.8px', fontSize: '11px' }}>{label}</div>
            {isUrl ? (
                value && value !== "N/A" ? (
                    <a href={value} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center gap-2 text-primary fw-semibold text-decoration-none" style={{ transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.opacity = 0.8} onMouseOut={e => e.currentTarget.style.opacity = 1}>
                        <FiLink size={14} /> View Link
                    </a>
                ) : (
                    <div className="fw-semibold text-dark">N/A</div>
                )
            ) : (
                <div className="fw-semibold text-dark fs-6" style={{ wordBreak: 'break-word' }}>{value || "N/A"}</div>
            )}
        </div>
    );

    const MetricBox = ({ label, value, prefix = "", suffix = "", accentColor = "#0d6efd", icon: Icon }) => (
        <div className="p-4 rounded-4 shadow-sm h-100 d-flex flex-column justify-content-between bg-white" style={{ borderLeft: `5px solid ${accentColor}`, borderTop: '1px solid #f0f0f0', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', transition: 'transform 0.2s', cursor: 'default' }} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-3px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
            <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="text-muted small fw-bold text-uppercase" style={{ letterSpacing: '1px', fontSize: '11px' }}>{label}</div>
                {Icon && <Icon size={18} color={accentColor} style={{ opacity: 0.7 }} />}
            </div>
            <div className="fw-bolder text-dark" style={{ fontSize: '1.75rem', letterSpacing: '-1px' }}>{prefix}{value}{suffix}</div>
        </div>
    );

    const SettingBadge = ({ label, active }) => (
        <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-3 border bg-light">
            <span className="fw-semibold text-dark">{label}</span>
            {active ? (
                <Badge color="success" className="px-3 py-2 rounded-pill d-flex align-items-center gap-1"><FiCheckCircle size={14} /> Active</Badge>
            ) : (
                <Badge color="secondary" className="px-3 py-2 rounded-pill d-flex align-items-center gap-1"><FiXCircle size={14} /> Disabled</Badge>
            )}
        </div>
    );

    return (
        <div className="pb-5 min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
            <PageName title={newItem?.name || "Product Details"} />

            {/* Hero Header Section */}
            <div className="bg-white border-bottom shadow-sm mb-4">
                <Container className="pt-4 pb-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <Button color="light" className="d-flex align-items-center gap-2 border shadow-sm fw-semibold" onClick={() => history.goBack()} style={{ borderRadius: '10px' }}>
                            <IoCloseSharp size={20} /> Back to Products
                        </Button>
                    </div>

                    <Row className="align-items-center g-4">
                        <Col md={3} lg={2} className="text-center text-md-start">
                            <div className="d-inline-block bg-white rounded-4 shadow p-2 position-relative" style={{ width: '160px', height: '160px', border: '1px solid #e9ecef' }}>
                                {newItem?.imageUrl && newItem.imageUrl !== "N/A" ? (
                                    <img src={newItem.imageUrl} alt={newItem.name} className="w-100 h-100 rounded-4 object-fit-cover" style={{ objectFit: 'contain' }} />
                                ) : (
                                    <div className="w-100 h-100 bg-light rounded-4 d-flex align-items-center justify-content-center text-muted">
                                        <FiImage size={48} />
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col md={9} lg={10}>
                            <div className="d-flex flex-column justify-content-center h-100 ps-md-3">
                                <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                                    <Badge color="primary" className="text-white px-3 py-2 rounded-pill shadow-sm border border-primary border-opacity-25" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)', color: '#ffffffff' }}>
                                        {newItem?.sku || "SKU N/A"}
                                    </Badge>
                                    <Badge color={newItem?.display ? "success" : "secondary"} className="px-3 py-2 rounded-pill shadow-sm d-flex align-items-center gap-1">
                                        {newItem?.display ? <><FiCheckCircle size={12} /> Active</> : <><FiXCircle size={12} /> Hidden</>}
                                    </Badge>
                                </div>
                                <h2 className="fw-bolder text-dark mb-3 lh-sm" style={{ letterSpacing: '-0.5px' }}>{newItem?.name || "Product Name Not Available"}</h2>
                                <div className="d-flex align-items-center gap-4 text-muted fw-medium flex-wrap">
                                    <span className="d-flex align-items-center gap-2"><FiBox size={16} /> {newItem?.mfg_name || "N/A"}</span>
                                    <span className="d-flex align-items-center gap-2"><FiTag size={16} /> {newItem?.category?.categoryName || "Uncategorized"}</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Container>
                {/* Metrics Row */}
                <Row className="g-4 mb-4">
                    <Col md={3}>
                        <MetricBox label="Price" value={newItem?.price ?? "0"} prefix="$" accentColor="#198754" icon={FiDollarSign} />
                    </Col>
                    <Col md={3}>
                        <MetricBox label="Cost" value={newItem?.stc_cost ?? "0"} prefix="$" accentColor="#dc3545" icon={FiDollarSign} />
                    </Col>
                    <Col md={3}>
                        <MetricBox label="In Stock" value={newItem?.stc_quantity ?? "0"} accentColor="#0dcaf0" icon={FiBox} />
                    </Col>
                    <Col md={3}>
                        <MetricBox label="Markup" value={newItem?.stc_markup ?? "0"} suffix="%" accentColor="#fd7e14" icon={FiActivity} />
                    </Col>
                </Row>

                <Row className="g-4 mb-4">
                    <Col lg={8}>
                        {/* Details Card */}
                        <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden">
                            <CardBody className="p-4 p-lg-5">
                                <SectionHeader icon={FiInfo} title="Product Identifiers" />
                                <Row className="mb-5">
                                    <Col md={4} sm={6}><DetailItem label="Identifiers" value={newItem?.identifiers && newItem.identifiers.length > 0 ? newItem.identifiers.join(", ") : "techmicro"} /></Col>
                                    <Col md={4} sm={6}><DetailItem label="Vendor Part" value={newItem?.vendor_part} /></Col>
                                    <Col md={4} sm={6}><DetailItem label="MFG Part" value={newItem?.item_mfg_part} /></Col>
                                    <Col md={4} sm={6}><DetailItem label="UPC" value={newItem?.item_upc} /></Col>
                                    <Col md={8} sm={12}><DetailItem label="Image URL" value={newItem?.imageUrl} isUrl={true} /></Col>
                                </Row>

                                <SectionHeader icon={FiTruck} title="Manufacturer & Classification" />
                                <Row className="mb-5">
                                    <Col md={4} sm={6}>
                                        <div className="mb-4">
                                            <div className="text-muted small fw-bold text-uppercase mb-1" style={{ letterSpacing: '0.8px', fontSize: '11px' }}>MFG Name</div>
                                            <div
                                                className="fw-bold text-primary pointer d-inline-flex align-items-center gap-1"
                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                onClick={() => {
                                                    localStorage.setItem('productSearch', newItem?.mfg_name || "");
                                                    localStorage.setItem('productPage', 1);
                                                    history.push('/admin/products');
                                                }}
                                                onMouseOver={e => e.currentTarget.style.opacity = 0.8}
                                                onMouseOut={e => e.currentTarget.style.opacity = 1}
                                            >
                                                <span style={{ borderBottom: '1px solid #0d6efd' }}>{newItem?.mfg_name ?? "N/A"}</span> <FiActivity size={14} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4} sm={6}><DetailItem label="Group" value={newItem?.mfg_group} /></Col>
                                    <Col md={4} sm={6}><DetailItem label="Com. Group" value={newItem?.mfg_com_group} /></Col>
                                    <Col md={4} sm={6}><DetailItem label="Category" value={newItem?.mfg_category} /></Col>
                                    <Col md={4} sm={6}><DetailItem label="Condition" value={newItem?.mfg_condition} /></Col>
                                </Row>

                                <SectionHeader icon={FiMaximize} title="Dimensions & Weight" />
                                <Row>
                                    <Col md={3} sm={6}><DetailItem label="Length (IN)" value={newItem?.dimensions?.length} /></Col>
                                    <Col md={3} sm={6}><DetailItem label="Height (IN)" value={newItem?.dimensions?.height} /></Col>
                                    <Col md={3} sm={6}><DetailItem label="Width (IN)" value={newItem?.dimensions?.width} /></Col>
                                    <Col md={3} sm={6}><DetailItem label="Weight (LBS)" value={newItem?.weight} /></Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        {/* Settings & Extra Pricing Card */}
                        <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden h-100">
                            <CardBody className="p-4 p-lg-5">
                                <SectionHeader icon={FiSettings} title="Operational Settings" />
                                <SettingBadge label="Display Status" active={newItem?.display} />
                                <SettingBadge label="Feed Enabled" active={newItem?.feed === "Enable"} />
                                <SettingBadge label="Reset Quantity" active={newItem?.reset_qty} />
                                <SettingBadge label="Price Sync (DNH)" active={newItem?.updatePriceFromDnh} />
                                <div className="d-flex align-items-center justify-content-between p-3 rounded-3 mb-3 border bg-light">
                                    <span className="fw-semibold text-dark">Guest Price Visibility</span>
                                    {newItem?.showPriceToGuests ? (
                                        <Badge color="primary" className="px-3 py-2 rounded-pill">Always Show</Badge>
                                    ) : newItem?.hidePriceFromGuests ? (
                                        <Badge color="danger" className="px-3 py-2 rounded-pill">Force Hide</Badge>
                                    ) : (
                                        <Badge color="secondary" className="px-3 py-2 rounded-pill">Inherit from MFG</Badge>
                                    )}
                                </div>

                                <div className="mt-5">
                                    <SectionHeader icon={FiDollarSign} title="Stock & Additional Pricing" />
                                    <Row>
                                        <Col xs={6}><DetailItem label="Shelf / Placement" value={newItem?.shelf_location || "Not Assigned"} /></Col>
                                        <Col xs={6}><DetailItem label="MSRP" value={`$${newItem?.stc_MSRP ?? "0"}`} /></Col>
                                        <Col xs={6}><DetailItem label="MAP" value={`$${newItem?.stc_map ?? "0"}`} /></Col>
                                    </Row>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Descriptions Card */}
                <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                    <CardBody className="p-4 p-lg-5">
                        <SectionHeader icon={FiFileText} title="Descriptions & Specifications" />
                        <Row className="g-4">
                            <Col md={6}>
                                <div className="text-muted small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.8px', fontSize: '11px' }}>Short Description</div>
                                <div className="p-4 bg-light rounded-4 border" style={{ minHeight: '120px' }}>
                                    <p className="mb-0 text-dark fw-medium lh-base">{newItem?.description || "No short description provided."}</p>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="text-muted small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.8px', fontSize: '11px' }}>Specifications</div>
                                <div className="p-4 bg-light rounded-4 border" style={{ minHeight: '120px' }}>
                                    <p className="mb-0 text-dark fw-medium lh-base" style={{ whiteSpace: 'pre-wrap' }}>
                                        {(newItem?.item_specification && typeof newItem.item_specification === 'object')
                                            ? stringifySpecs(newItem.item_specification)
                                            : (newItem?.item_specification || "No specifications provided.")}
                                    </p>
                                </div>
                            </Col>
                            <Col md={12}>
                                <div className="text-muted small fw-bold text-uppercase mb-2 mt-2" style={{ letterSpacing: '0.8px', fontSize: '11px' }}>Long Description (HTML Overview)</div>
                                <div
                                    className="p-4 bg-light rounded-4 border text-dark lh-lg"
                                    style={{ minHeight: '150px' }}
                                    dangerouslySetInnerHTML={{ __html: newItem?.long_description || "<span class='text-muted italic'>No long description available.</span>" }}
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Logs Card */}
                {newItem?.notes?.length > 0 && (
                    <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                        <CardBody className="p-4 p-lg-5">
                            <SectionHeader icon={FiCalendar} title="Internal Logs & Notes" />
                            <div className="border rounded-4 overflow-hidden">
                                <LogTable orderNotes={newItem?.notes?.sort((a, b) => b?.createdAt - a?.createdAt)} />
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Reviews Card */}
                <Card className="border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                    <div className="bg-white p-4 p-lg-5 pb-0">
                        <SectionHeader icon={FiMessageSquare} title="Customer Reviews" />
                    </div>
                    <CardBody className="p-0">
                        {loadingReviews ? (
                            <div className="p-4">
                                <SkeletonLoader height="50px" count={3} borderRadius="8px" />
                            </div>
                        ) : reviews.length > 0 ? (
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="px-4 py-3 border-bottom-0 small text-uppercase fw-bold text-muted" style={{ letterSpacing: '1px' }}>User</th>
                                            <th className="px-4 py-3 border-bottom-0 small text-uppercase fw-bold text-muted" style={{ letterSpacing: '1px' }}>Rating</th>
                                            <th className="px-4 py-3 border-bottom-0 small text-uppercase fw-bold text-muted" style={{ letterSpacing: '1px' }}>Comment</th>
                                            <th className="px-4 py-3 border-bottom-0 small text-uppercase fw-bold text-muted" style={{ letterSpacing: '1px' }}>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reviews.map((review, i) => (
                                            <tr key={i} className="border-bottom">
                                                <td className="px-4 py-4 fw-bold text-dark">{review.name || "Anonymous"}</td>
                                                <td className="px-4 py-4">
                                                    <div className="d-flex align-items-center gap-1 text-warning fs-5">
                                                        {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-muted fw-medium" style={{ maxWidth: '400px' }}>{review.comment || "No comment"}</td>
                                                <td className="px-4 py-4 text-muted fw-medium small">
                                                    {review.date || (review.createdAt?.toDate?.() || new Date(review.createdAt)).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="p-5 text-center text-muted fw-medium bg-light border-top">No reviews available for this product yet.</div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default ProductDetails;


